import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './style.css';
import { requestAttributeFocus } from '../../../../redux/reducers/global/inspector/inspectorSlice';

const normalizeHexColor = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  let hex = value.trim();
  if (!hex.startsWith('#')) {
    hex = `#${hex}`;
  }
  if (/^#([0-9a-fA-F]{3})$/.test(hex)) {
    hex = `#${hex
      .slice(1)
      .split('')
      .map((ch) => ch + ch)
      .join('')}`;
  }
  if (!/^#([0-9a-fA-F]{6})$/.test(hex)) {
    return fallback;
  }
  return hex.toLowerCase();
};

const clampAlpha = (value, fallback = 1) => {
  if (value === undefined || value === null) {
    return fallback;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return fallback;
  }
  if (numeric < 0) return 0;
  if (numeric > 1) return 1;
  return numeric;
};

const hexToRgb = (hex) => {
  if (typeof hex !== 'string' || !/^#([0-9a-fA-F]{6})$/.test(hex)) {
    return null;
  }
  const numeric = parseInt(hex.slice(1), 16);
  return {
    r: (numeric >> 16) & 255,
    g: (numeric >> 8) & 255,
    b: numeric & 255,
  };
};

const PX_PER_CM = 37.7952755906;
const BASE_PX = 100;
const BASE_MIN_WIDTH = 220;
const BASE_MAX_WIDTH = 300;
const BASE_MIN_HEIGHT = 100;
const BASE_PADDING_X = 18;
const BASE_PADDING_Y = 16;

const clampScaleFactor = (scaleCm, fallbackCm) => {
  const fallback = typeof fallbackCm === 'number' && fallbackCm > 0 ? fallbackCm : BASE_PX / PX_PER_CM;
  const effective = typeof scaleCm === 'number' && scaleCm > 0 ? scaleCm : fallback;
  const factor = (effective * PX_PER_CM) / BASE_PX;
  return Math.max(0.4, Math.min(6, factor));
};

export const TextBoxNode = ({ id, data, selected }) => {
  const dispatch = useDispatch();
  const attributes = data?.attributes || {};
  const textAttr = attributes.textContent;
  const backgroundAttr = attributes.backgroundColor;
  const backgroundAlphaAttr = attributes.backgroundAlpha;
  const textColorAttr = attributes.textColor;
  const textAlignAttr = attributes.textAlign;
  const textBoldAttr = attributes.textBold;

  const globalScaleCm = useSelector((state) => state.view.scaleCm);
  const globalLabelFontPx = useSelector((state) => state.view.labelFontPx) || 38;

  const textValue = typeof textAttr?.value === 'string' ? textAttr.value : '';
  const displayText = textValue.trim().length > 0 ? textValue : 'Agrega texto en el inspector';
  const isPlaceholder = textValue.trim().length === 0;
  const textSizePx = useMemo(() => {
    const custom = data?.textSizePx;
    return typeof custom === 'number' && custom > 0 ? custom : globalLabelFontPx;
  }, [data?.textSizePx, globalLabelFontPx]);
  const scaleFactor = useMemo(
    () => clampScaleFactor(data?.scaleCm, globalScaleCm),
    [data?.scaleCm, globalScaleCm]
  );

  const backgroundAlpha = useMemo(
    () => clampAlpha(backgroundAlphaAttr?.value, 1),
    [backgroundAlphaAttr?.value]
  );

  const backgroundColor = useMemo(() => {
    const normalizedHex = normalizeHexColor(backgroundAttr?.value, '#FFFFFF');
    const alpha = clampAlpha(backgroundAlpha, 1);
    if (alpha <= 0) {
      return 'transparent';
    }
    if (alpha >= 1) {
      return normalizedHex;
    }
    const rgb = hexToRgb(normalizedHex);
    if (!rgb) {
      return normalizedHex;
    }
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${Number(alpha).toFixed(2)})`;
  }, [backgroundAttr?.value, backgroundAlpha]);

  const textColor = useMemo(
    () => normalizeHexColor(textColorAttr?.value, '#111827'),
    [textColorAttr?.value]
  );
  const textAlign = useMemo(() => {
    const raw = typeof textAlignAttr?.value === 'string' ? textAlignAttr.value.trim().toLowerCase() : '';
    if (raw === 'left' || raw === 'center' || raw === 'right') return raw;
    return 'center';
  }, [textAlignAttr?.value]);
  const textBold = textBoldAttr?.value === true || textBoldAttr?.value === 'true';

  const nodeStyle = useMemo(() => {
    const widthPx = BASE_MIN_WIDTH * scaleFactor;
    const maxWidthPx = BASE_MAX_WIDTH * scaleFactor;
    const paddingY = BASE_PADDING_Y * scaleFactor;
    const paddingX = BASE_PADDING_X * scaleFactor;
    return {
      backgroundColor,
      color: textColor,
      minWidth: `${widthPx}px`,
      maxWidth: `${maxWidthPx}px`,
      height: 'fit-content',
      padding: `${paddingY}px ${paddingX}px`,
      fontSize: `${textSizePx}px`,
      fontWeight: textBold ? 700 : 600,
      textAlign,
      justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
    };
  }, [backgroundColor, textColor, scaleFactor, textSizePx, textAlign, textBold]);

  const handleDoubleClick = useCallback(() => {
    dispatch(
      requestAttributeFocus({
        nodeId: id,
        attributeName: 'textContent',
        requestedAt: Date.now(),
      })
    );
  }, [dispatch, id]);

  return (
    <div
      className={`textbox-node${selected ? ' is-selected' : ''}`}
      style={nodeStyle}
      onDoubleClick={handleDoubleClick}
    >
      <div className={`textbox-node__content${isPlaceholder ? ' is-placeholder' : ''}`}>
        {displayText}
      </div>
    </div>
  );
};

export default TextBoxNode;
